import { useBooleanFlagValue } from '@openfeature/react-sdk';
import Form from '@risk-smart/themed-cloudscape-components/form';
import { Suspense } from 'react';
import type { FieldValues } from 'react-hook-form';
import TabHeader from 'src/components/tab-header';

import { useObjectToolbarFormStore } from '@/blocks/object-toolbar/useObjectToolbarFormStore';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { FormInner } from './FormInner';
import { FormMenu } from './FormMenu';
import styles from './style.module.scss';
import type { FormTemplateProps } from './types';

export const PageWrapper = <TFieldValues extends FieldValues>(
  props: FormTemplateProps<TFieldValues>
) => {
  const { formId, children, actions, readOnly, parentType } = props;
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const objectToolbarEnabled = useBooleanFlagValue('object_toolbar', false);
  // Only relocate when a toolbar is actually mounted on this page — the
  // `object_toolbar` flag can be on globally while a given page renders no
  // toolbar. This must match FormMenu's own relocate condition; otherwise we
  // drop the header and mount FormMenu in the body while FormMenu (seeing no
  // toolbar) renders its visible 3-dot there — left-aligned above the first
  // section instead of in the header (CUS-150).
  const toolbarMounted = useObjectToolbarFormStore((s) => s.toolbarMounted);

  // Both flags on AND a toolbar mounted → the page-header object toolbar owns
  // the title and the relocated "Edit form" action, so the FormMenu 3-dot is
  // suppressed. The TabHeader would then be an empty title with no actions —
  // drop the whole header so it doesn't reserve blank space above the first
  // section. FormMenu is still mounted (in the body, where it renders nothing
  // visible in relocate mode) so its effect keeps publishing the form-editor
  // opener to the toolbar bridge store and it can host the FormEditorDialog.
  const relocatedToToolbar =
    !!parentType &&
    formSectionsEnabled &&
    objectToolbarEnabled &&
    toolbarMounted;

  const stringHeader =
    typeof props.header === 'string' ? (
      <TabHeader
        actions={
          !readOnly && parentType ? <FormMenu parentType={parentType} /> : <></>
        }
      >
        {parentType && formSectionsEnabled ? '' : props.header}
      </TabHeader>
    ) : (
      props.header
    );

  const header = relocatedToToolbar ? undefined : stringHeader;

  return (
    <form
      data-testid={props.testId}
      onSubmit={(e) => e.preventDefault()}
      id={formId}
      name={formId}
      className={styles.noFieldsetBorder}
    >
      <Suspense>
        <Form actions={<></>} header={header} secondaryActions={actions}>
          <FormInner>
            {/* Relocate mode: TabHeader is gone, so mount FormMenu here. It
                renders nothing visible (the 3-dot is suppressed) but its effect
                publishes the form-editor opener to the toolbar bridge store and
                it hosts the FormEditorDialog. */}
            {relocatedToToolbar && !readOnly && parentType ? (
              <FormMenu parentType={parentType} />
            ) : null}
            <div className={'flex gap-5 justify-between'}>
              <div className={'flex-1'}>{children}</div>
              {props.aside ? (
                <div className={styles.formSidebar}>{props.aside}</div>
              ) : null}
            </div>
          </FormInner>
        </Form>
      </Suspense>
    </form>
  );
};
